import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { Box, Button, ChipButton, Row, Typography } from 'seagull';
import { AIChatPane } from '~/components/ai/AIChatPane/AIChatPane';
import { AiWorkingOnIt } from '~/components/ai/AiWorkingOnIt/AiWorkingOnIt';
import { PromptInputField } from '~/components/ai/PromptInputField/PromptInputField';
import { ChatTextMessage } from '~/components/ai/shared/ChatTextMessage/ChatTextMessage';
import { SparkleIcon } from '~/components/ai/shared/SparkleIcon/SparkleIcon';
import { UserMessage } from '~/components/ai/shared/UserMessage/UserMessage';
import { useLocaleKeys } from '~/i18n';
import styles from './AnalystAgentPane.module.scss';
import { ANALYST_INTERCOM_VERTICAL_PADDING, GetAnalystAgentPaneTID, selectStarterPrompts } from './AnalystAgentPane.utils';
import { useAnalystConversation } from './hooks/useAnalystConversation';
import { AnalystChartMessage } from './messages/AnalystChartMessage';
import { AnalystFeedbackBar } from './messages/AnalystFeedbackBar';
import { AnalystGuideAnswerMessage } from './messages/AnalystGuideAnswerMessage/AnalystGuideAnswerMessage';
import { AnalystResultTableMessage } from './messages/AnalystResultTableMessage';
import { AnalystSummaryMessage } from './messages/AnalystSummaryMessage';
import { shouldShowFeedbackBar, useAnalystChatStore } from './store/analystChatStore';
const STARTER_PROMPT_POOL_INDEXES = [
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    14,
    15,
    16,
    17,
    18,
    19
];
const STARTER_PROMPT_DISPLAY_COUNT = 4;
export const AnalystAgentPane = (param)=>{
    let { isOpen: controlledIsOpen, onOpenChange, pollIntervalMs, feedbackDismissMs, starterPromptRng, testId = '' } = param;
    var _lastMessage_part_suggestions, _lastMessage_part_suggestions1;
    const { t } = useLocaleKeys('ai.analyst');
    const { AnalystAgentPaneTestIds } = useMemo(()=>GetAnalystAgentPaneTID(testId), [
        testId
    ]);
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledIsOpen != null;
    const isOpen = isControlled ? controlledIsOpen : internalOpen;
    const setOpen = (open)=>{
        if (!isControlled) setInternalOpen(open);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
    };
    const { send, retry, reset, submitFeedback, markAnswerRated } = useAnalystConversation({
        pollIntervalMs
    });
    const messages = useAnalystChatStore((s)=>s.messages);
    const isThinking = useAnalystChatStore((s)=>s.isThinking);
    const thinkingLabel = useAnalystChatStore((s)=>s.thinkingLabel);
    const transportError = useAnalystChatStore((s)=>s.transportError);
    const isRecovering = useAnalystChatStore((s)=>s.isRecovering);
    const recoveryFailed = useAnalystChatStore((s)=>s.recoveryFailed);
    const recoveryRetryable = useAnalystChatStore((s)=>s.recoveryRetryable);
    const feedbackEligibleMessageId = useAnalystChatStore((s)=>s.feedbackEligibleMessageId);
    const feedbackEligibleRenderedId = useAnalystChatStore((s)=>s.feedbackEligibleRenderedId);
    const submittedFeedbackIds = useAnalystChatStore((s)=>s.submittedFeedbackIds);
    const showFeedback = shouldShowFeedbackBar(feedbackEligibleMessageId, submittedFeedbackIds);
    const [text, setText] = useState('');
    const [jumpToBottomToken, setJumpToBottomToken] = useState(0);
    const jumpToBottom = ()=>setJumpToBottomToken((token)=>token + 1);
    const onSubmitPrompt = ()=>{
        send(text);
        jumpToBottom();
        setText('');
    };
    const errorCopy = (code)=>{
        switch(code){
            case 'query_timeout':
                return t.errors.queryTimeout();
            case 'service_busy':
                return t.errors.serviceBusy();
            case 'invalid_query':
                return t.errors.invalidQuery();
            case 'no_query_formed':
                return t.errors.noQueryFormed();
            case 'internal_error':
                return t.errors.internalError();
            default:
                return t.errors.internalError();
        }
    };
    const lastMessage = messages[messages.length - 1];
    const lastIsRetryableError = (lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.role) === 'assistant' && lastMessage.part.type === 'error' && lastMessage.part.retryable;
    const canRetry = transportError != null || lastIsRetryableError === true;
    const activeClarify = (lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.role) === 'assistant' && lastMessage.part.type === 'clarifying_question' && ((_lastMessage_part_suggestions = lastMessage.part.suggestions) !== null && _lastMessage_part_suggestions !== void 0 ? _lastMessage_part_suggestions : []).length > 0 ? {
        id: lastMessage.id,
        suggestions: ((_lastMessage_part_suggestions1 = lastMessage.part.suggestions) !== null && _lastMessage_part_suggestions1 !== void 0 ? _lastMessage_part_suggestions1 : []).slice(0, 5)
    } : null;
    const hasMessages = messages.length > 0;
    const showEmptyState = !hasMessages && !isRecovering && !recoveryFailed;
    const starterPromptPool = STARTER_PROMPT_POOL_INDEXES.map((index)=>t.emptyState.starterPrompts[index]());
    const starterPrompts = useMemo(()=>selectStarterPrompts(starterPromptPool, STARTER_PROMPT_DISPLAY_COUNT, starterPromptRng), // WHY: pick once per empty-state mount; the pool strings and rng are stable inputs, so depending on them would reshuffle the chips on every render.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    [
        showEmptyState
    ]);
    return /*#__PURE__*/ _jsx(AIChatPane, {
        testId: testId,
        isOpen: isOpen,
        dragResize: true,
        rootClassName: cn(styles.root, isOpen ? styles.open : styles.collapsed),
        intercom: {
            collapsedVerticalPadding: ANALYST_INTERCOM_VERTICAL_PADDING
        },
        jumpToBottomToken: jumpToBottomToken,
        header: {
            mode: 'collapsible',
            icon: /*#__PURE__*/ _jsx(SparkleIcon, {
                testId: AnalystAgentPaneTestIds.HeaderIcon()
            }),
            title: t.header.title(),
            collapsedTitle: t.collapsedLabel(),
            betaLabel: t.header.beta(),
            clearChat: hasMessages ? {
                label: t.clearChat(),
                onClearChat: ()=>reset(),
                isClearDisabled: isThinking || isRecovering,
                clearChatBiName: 'analyst_new_conversation'
            } : null,
            onToggle: ()=>setOpen(!isOpen),
            onExpand: ()=>setOpen(true),
            toggleBiName: isOpen ? 'toggle_analyst_pane' : 'open_analyst_agent',
            collapseChevronOrientation: 'vertical'
        },
        thread: /* eslint-disable-next-line r15/forbid-elements -- scss-driven thread wrapper owns the inter-part gap (the shell's contentRef is bare); seagull Box's flex defaults would override the answerParts column layout */ /*#__PURE__*/ _jsxs("div", {
            className: cn(styles.threadContent, styles.answerParts),
            "data-testid": AnalystAgentPaneTestIds.Thread(),
            children: [
                showEmptyState && /*#__PURE__*/ _jsxs(Box, {
                    className: styles.emptyState,
                    testId: AnalystAgentPaneTestIds.EmptyState(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "normal",
                            testId: AnalystAgentPaneTestIds.EmptyStateTitle(),
                            children: t.emptyState.title()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: AnalystAgentPaneTestIds.EmptyStateSubtitle(),
                            children: "".concat(t.emptyState.subtitle(), " ").concat(t.emptyState.tryAsking())
                        }),
                        /*#__PURE__*/ _jsx(Row, {
                            gap: 8,
                            wrap: true,
                            className: styles.suggestions,
                            children: starterPrompts.map((prompt, index)=>/*#__PURE__*/ _jsx(ChipButton, {
                                    className: styles.suggestion,
                                    bi: {
                                        name: 'analyst_starter_prompt'
                                    },
                                    skin: "subtle",
                                    testId: AnalystAgentPaneTestIds.StarterPrompt(index),
                                    onClick: ()=>{
                                        send(prompt);
                                        jumpToBottom();
                                    },
                                    children: prompt
                                }, prompt))
                        })
                    ]
                }),
                messages.map((message)=>{
                    if (message.role === 'user') {
                        return /*#__PURE__*/ _jsx(Box, {
                            className: styles.userMessage,
                            testId: AnalystAgentPaneTestIds.UserMessage(message.id),
                            children: /*#__PURE__*/ _jsx(UserMessage, {
                                messageId: message.id,
                                content: {
                                    type: 'prompt',
                                    text: message.text
                                }
                            })
                        }, message.id);
                    }
                    return /*#__PURE__*/ _jsxs(Box, {
                        className: cn(styles.assistantMessage, styles.answerParts),
                        testId: AnalystAgentPaneTestIds.AssistantMessage(message.id),
                        children: [
                            message.part.type === 'nl_summary' && /*#__PURE__*/ _jsx(AnalystSummaryMessage, {
                                messageId: message.id,
                                text: message.part.text
                            }),
                            message.part.type === 'error' && /*#__PURE__*/ _jsx(ChatTextMessage, {
                                messageId: message.id,
                                content: {
                                    type: 'chat-text',
                                    text: errorCopy(message.part.code)
                                }
                            }),
                            message.part.type === 'result_table' && /*#__PURE__*/ _jsx(AnalystResultTableMessage, {
                                messageId: message.id,
                                part: message.part
                            }),
                            message.part.type === 'chart' && /*#__PURE__*/ _jsx(AnalystChartMessage, {
                                messageId: message.id,
                                part: message.part
                            }),
                            message.part.type === 'guide_answer' && /*#__PURE__*/ _jsx(AnalystGuideAnswerMessage, {
                                messageId: message.id,
                                part: message.part,
                                onFollowUpClick: (label)=>{
                                    send(label);
                                    jumpToBottom();
                                }
                            }),
                            message.part.type === 'clarifying_question' && /*#__PURE__*/ _jsxs(Box, {
                                className: styles.clarify,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        testId: AnalystAgentPaneTestIds.ClarifyQuestion(message.id),
                                        showWhiteSpaces: true,
                                        applyMarkdownBold: true,
                                        children: message.part.question
                                    }),
                                    (activeClarify === null || activeClarify === void 0 ? void 0 : activeClarify.id) === message.id && /*#__PURE__*/ _jsx(Row, {
                                        gap: 8,
                                        wrap: true,
                                        className: styles.suggestions,
                                        testId: AnalystAgentPaneTestIds.ClarifySuggestionsCard(message.id),
                                        children: activeClarify.suggestions.map((suggestion, i)=>/*#__PURE__*/ _jsx(ChipButton, {
                                                className: styles.suggestion,
                                                bi: {
                                                    name: 'analyst_clarify_suggestion'
                                                },
                                                skin: "subtle",
                                                testId: AnalystAgentPaneTestIds.ClarifySuggestion(message.id, i),
                                                onClick: ()=>{
                                                    send(suggestion);
                                                    jumpToBottom();
                                                },
                                                children: suggestion
                                            }, suggestion))
                                    })
                                ]
                            }),
                            message.id === feedbackEligibleRenderedId && showFeedback && feedbackEligibleMessageId != null && /*#__PURE__*/ _jsx(AnalystFeedbackBar, {
                                testId: AnalystAgentPaneTestIds.FeedbackBar(),
                                dismissDelayMs: feedbackDismissMs,
                                onSubmit: (rating, comment)=>submitFeedback(feedbackEligibleMessageId, rating, comment),
                                onDismiss: ()=>markAnswerRated(feedbackEligibleMessageId)
                            })
                        ]
                    }, message.id);
                }),
                (isThinking || isRecovering) && /*#__PURE__*/ _jsx(AiWorkingOnIt, {
                    className: styles.thinkingIndicator,
                    label: isRecovering ? t.recovery.recovering() : thinkingLabel !== null && thinkingLabel !== void 0 ? thinkingLabel : undefined,
                    testId: AnalystAgentPaneTestIds.ThinkingIndicator()
                })
            ]
        }),
        footer: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                canRetry && !recoveryFailed && /*#__PURE__*/ _jsxs(Box, {
                    className: styles.errorFooter,
                    children: [
                        transportError != null && /*#__PURE__*/ _jsx(Typography, {
                            skin: "error",
                            testId: AnalystAgentPaneTestIds.TransportError(),
                            children: t.transport.message()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'analyst_retry'
                            },
                            skin: "primaryOutline",
                            size: "small",
                            testId: AnalystAgentPaneTestIds.RetryButton(),
                            onClick: ()=>retry(),
                            children: t.retry()
                        })
                    ]
                }),
                recoveryFailed && /*#__PURE__*/ _jsxs(Box, {
                    className: styles.errorFooter,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: "error",
                            testId: AnalystAgentPaneTestIds.RecoveryMessage(),
                            children: recoveryRetryable ? t.transport.message() : t.recovery.notRetrievable()
                        }),
                        recoveryRetryable && /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'analyst_recovery_retry'
                            },
                            skin: "primaryOutline",
                            size: "small",
                            testId: AnalystAgentPaneTestIds.RecoveryRetryButton(),
                            onClick: ()=>retry(),
                            children: t.retry()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.inputBlock,
                    children: /*#__PURE__*/ _jsx(PromptInputField, {
                        testId: AnalystAgentPaneTestIds.Input(),
                        value: text,
                        onChange: setText,
                        onSubmit: onSubmitPrompt,
                        placeholder: t.input.placeholder(),
                        disabled: isThinking || isRecovering,
                        submitDisabled: isThinking || isRecovering || text.trim().length === 0
                    })
                })
            ]
        })
    });
};
